interface RelevanceSortToggleProps {
  /** Is the listing currently showing ranked order? */
  active: boolean;
  /** Turn relevance on (inactive) or hand the listing back to `fallbackLabel` (active). */
  onToggle: () => void;
  /** The sort a click returns to, named so the button says what it will do. */
  fallbackLabel: string;
  className?: string;
}

/**
 * The one control that surfaces Algolia's ranking on a listing whose sort is
 * otherwise driven by clickable column headers.
 *
 * Relevance is not a column — there is no cell to put an arrow in — so it gets
 * its own chip beside the search box rather than a seventh header. The callers
 * render it only while a query is active, which is the only time a ranking
 * exists; see `services/relevanceSort.ts`.
 *
 * Clicking it either way counts as a MANUAL PICK, so it also switches off the
 * auto-relevance behaviour for the rest of the session — which is the point of
 * having the control at all: an admin who wants alphabetical results while
 * searching clicks once and stops being second-guessed.
 */
export function RelevanceSortToggle({
  active,
  onToggle,
  fallbackLabel,
  className = '',
}: RelevanceSortToggleProps) {
  return (
    <button
      type="button"
      onClick={onToggle}
      aria-pressed={active}
      title={
        active
          ? `Sorted by best match. Click to sort by ${fallbackLabel} instead.`
          : `Sort by best match instead of ${fallbackLabel}.`
      }
      className={`shrink-0 px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${
        active
          ? 'bg-brand-100 text-brand-700 hover:bg-brand-200'
          : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
      } ${className}`}
    >
      ✨ Relevance
    </button>
  );
}
